import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  TextField, MenuItem, Chip,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { useActivityLog } from '../hooks/useActivityLog';
import type { ActivityLogFilters } from '../services/operationsService';

const RESULT_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  success: 'success',
  failed: 'error',
  error: 'error',
  unknown: 'default',
};

const ActivityLogPanel: React.FC = () => {
  const [customerFilter, setCustomerFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const filters = useMemo<ActivityLogFilters>(() => {
    const f: ActivityLogFilters = { limit: 100 };
    if (customerFilter) f.customer_id = customerFilter;
    if (actionFilter) f.action = actionFilter;
    return f;
  }, [customerFilter, actionFilter]);

  const { data, loading, error } = useActivityLog(filters);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <InlineAlert severity="error">{error}</InlineAlert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label="Customer ID"
          size="small"
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Action"
          size="small"
          select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="launch">Launch</MenuItem>
          <MenuItem value="update">Update</MenuItem>
          <MenuItem value="terminate">Terminate</MenuItem>
        </TextField>
      </Box>

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 'calc(90vh - 320px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>ServiceComponent</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.activities.map((entry, idx) => (
                <TableRow key={`${entry.deployment_id}-${entry.timestamp}-${idx}`} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(entry.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{entry.customer_id}</TableCell>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>{entry.product}</TableCell>
                  <TableCell>{entry.service}</TableCell>
                  <TableCell>{entry.actor}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.result}
                      size="small"
                      color={RESULT_COLORS[entry.result] || 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {(!data || data.activities.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No activity found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.total_count > 0 && (
          <Box sx={{ p: 1, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Showing {data.activities.length} of {data.total_count} entries
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ActivityLogPanel;
